import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, api } from '@/lib/api';
import { getTranslatedError } from '@/lib/error-translator';
import { getSafeRedirectPath } from '@/lib/url-utils';
import { type LoginFormData, getLoginSchema } from '@/lib/validations/auth';
import { type BctTheme, type NotificationPreference, useAuthStore } from '@/stores/auth';

export function Login() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const { setUser, setAccessToken } = useAuthStore();

  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof LoginFormData, string>>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof LoginFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    if (generalError) {
      setGeneralError(null);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});
    setGeneralError(null);

    // Validate form data
    const result = getLoginSchema(t).safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof LoginFormData, string>> = {};
      result.error.issues.forEach((issue) => {
        const field = issue.path[0] as keyof LoginFormData;
        fieldErrors[field] = issue.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post<{ accessToken: string }>('/auth/login', formData);

      if (response.data?.accessToken) {
        setAccessToken(response.data.accessToken);

        // Fetch user data
        const userResponse = await api.get<{
          id: string;
          email: string;
          name: string;
          role: 'BCT_ADMIN' | 'BCT_PM' | 'BCT_CONSULTANT' | 'CUSTOMER_ADMIN' | 'CUSTOMER_USER';
          tenantId: string | null;
          profilePictureUrl: string | null;
          authProvider: 'EMAIL' | 'MICROSOFT' | 'GOOGLE';
          isActive: boolean;
          preferredLanguage?: string;
          notificationPreference?: NotificationPreference;
          // Contact & Availability fields
          phoneNumber?: string | null;
          phoneVisible?: boolean;
          weeklyAttendance?: Record<string, string | null> | null;
          attendanceVisible?: boolean;
          organizationTheme?: BctTheme;
        }>('/auth/me');

        if (userResponse.data) {
          setUser(userResponse.data);
        }

        // Redirect to intended destination or dashboard
        // Validate the redirect path to prevent open redirect vulnerabilities
        const storedPath = sessionStorage.getItem('auth:redirect');
        sessionStorage.removeItem('auth:redirect');
        const redirectPath = getSafeRedirectPath(storedPath, '/dashboard');
        navigate(redirectPath, { replace: true });
      }
    } catch (error) {
      if (error instanceof ApiError) {
        // Use error translator to get translated error messages
        const translatedError = getTranslatedError(t, error.response ?? error);
        setGeneralError(translatedError);
      } else {
        setGeneralError(t('auth:login.unexpectedError'));
      }
    } finally {
      setIsLoading(false);
    }
  }

  function handleSsoLogin(provider: 'microsoft' | 'google') {
    // Store current path for redirect after SSO
    sessionStorage.setItem(
      'auth:redirect',
      sessionStorage.getItem('auth:redirect') ?? '/dashboard',
    );
    window.location.href = `/api/v1/auth/${provider}`;
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center px-4">
      {/* Background images for light/dark mode */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat dark:hidden"
        style={{ backgroundImage: 'url(/images/login-bg-light.jpeg)' }}
      />
      <div
        className="absolute inset-0 hidden bg-cover bg-center bg-no-repeat dark:block"
        style={{ backgroundImage: 'url(/images/login-bg-dark.jpeg)' }}
      />

      <div className="absolute right-4 top-4 z-10">
        <ThemeToggle />
      </div>

      <Card className="relative z-10 w-full max-w-md bg-white/95 dark:bg-card/95 backdrop-blur-sm">
        <CardHeader className="space-y-1 text-center">
          <div className="flex items-center justify-center gap-3">
            <img src="/images/logo-corsa.png" alt="PreGo" className="h-12 w-auto" />
            <span className="text-2xl font-bold tracking-tight text-foreground">PreGo!</span>
          </div>
          <CardDescription>{t('auth:login.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {generalError && (
            <div
              className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive"
              role="alert"
            >
              {generalError}
            </div>
          )}

          <div className="grid gap-2">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => handleSsoLogin('microsoft')}
            >
              <svg
                className="mr-2 h-4 w-4"
                viewBox="0 0 21 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-label="Microsoft logo"
                role="img"
              >
                <path d="M10 0H0V10H10V0Z" fill="#F25022" />
                <path d="M21 0H11V10H21V0Z" fill="#7FBA00" />
                <path d="M10 11H0V21H10V11Z" fill="#00A4EF" />
                <path d="M21 11H11V21H21V11Z" fill="#FFB900" />
              </svg>
              {t('auth:login.signInWithMicrosoft')}
            </Button>
            {import.meta.env.VITE_GOOGLE_AUTH_ENABLED === 'true' && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => handleSsoLogin('google')}
              >
                <svg
                  className="mr-2 h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-label="Google logo"
                  role="img"
                >
                  <path
                    d="M22.56 12.25C22.56 11.47 22.49 10.72 22.36 10H12V14.26H17.92C17.66 15.63 16.88 16.79 15.71 17.57V20.34H19.28C21.36 18.42 22.56 15.6 22.56 12.25Z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23C14.97 23 17.46 22.02 19.28 20.34L15.71 17.57C14.73 18.23 13.48 18.63 12 18.63C9.13 18.63 6.71 16.69 5.84 14.09H2.18V16.94C3.99 20.53 7.7 23 12 23Z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09C5.62 13.43 5.49 12.73 5.49 12C5.49 11.27 5.62 10.57 5.84 9.91V7.06H2.18C1.43 8.55 1 10.22 1 12C1 13.78 1.43 15.45 2.18 16.94L5.84 14.09Z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.37C13.62 5.37 15.06 5.93 16.21 7.02L19.36 3.87C17.45 2.09 14.97 1 12 1C7.7 1 3.99 3.47 2.18 7.06L5.84 9.91C6.71 7.31 9.13 5.37 12 5.37Z"
                    fill="#EA4335"
                  />
                </svg>
                {t('auth:login.signInWithGoogle')}
              </Button>
            )}
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white dark:bg-card px-2 text-muted-foreground">
                {t('auth:login.orContinueWithEmail')}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth:login.email')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder={t('auth:login.emailPlaceholder')}
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
              {errors.email && (
                <p id="email-error" className="text-sm text-destructive">
                  {errors.email}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t('auth:login.password')}</Label>
                <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                  {t('auth:login.forgotPassword')}
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={
                    showPassword ? t('auth:login.hidePassword') : t('auth:login.showPassword')
                  }
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p id="password-error" className="text-sm text-destructive">
                  {errors.password}
                </p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('auth:login.signIn')}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            {t('auth:login.noAccount')}{' '}
            <Link to="/register" className="text-primary hover:underline">
              {t('auth:login.createAccount')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
